import { useState } from 'react';
import { Link, NavLink, useLocation, useSearchParams } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/beui';
import {
  LayoutTemplate,
  Layers,
  Files,
  Star,
  Clock,
  CreditCard,
  Settings,
  Plus,
  MoreHorizontal,
  Edit3,
  Trash2,
} from '@/components/icons';
import {
  useFoldersQuery,
  useSubscriptionQuery,
  useCreateFolderMutation,
  useUpdateFolderMutation,
  useDeleteFolderMutation,
} from '../../hooks/queries';
import { useAuth } from '../../pages/Auth/AuthProvider';
import { CreateFolderModal, EditFolderModal, DeleteConfirmModal } from '../ui';
import { FolderContextMenu } from './FolderContextMenu';
import { DiagramIcon } from '../diagramIcons';
import type { Folder } from '../../types';

const base =
  'ss-press flex h-[33px] items-center gap-2.5 rounded-md px-2.5 text-[13.5px] transition-colors';
const idle = 'text-foreground hover:bg-muted';
const active = 'bg-muted font-semibold';

function GroupLabel({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between px-2 pb-1 pt-3.5">
      <span className="text-[11px] font-semibold text-muted-foreground/80">{children}</span>
      {action}
    </div>
  );
}

/** Rename/Delete menu for a folder row. Reveals on row hover / focus / open. */
function FolderMenu({ name, onRename, onDelete }: { name: string; onRename: () => void; onDelete: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <button
          onClick={(e) => {
            e.stopPropagation();
          }}
          aria-label={`Actions for ${name}`}
          className="grid h-6 w-6 flex-none place-items-center rounded text-muted-foreground opacity-0 outline-none transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring group-hover/folder:opacity-100 aria-expanded:bg-muted aria-expanded:opacity-100"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]" ariaLabel={`Actions for ${name}`}>
        <DropdownMenuItem onSelect={onRename}>
          <Edit3 className="opacity-80" /> Rename
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem tone="destructive" onSelect={onDelete}>
          <Trash2 /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Persistent left navigation rail. Library + folder links drive the Home filter via ?view / ?folder. */
export function SideNav() {
  const { isAuthenticated } = useAuth();
  const { data: folders = [] } = useFoldersQuery(isAuthenticated);
  const sub = useSubscriptionQuery();
  const [params] = useSearchParams();
  const location = useLocation();

  const createFolderMut = useCreateFolderMutation();
  const updateFolderMut = useUpdateFolderMutation();
  const deleteFolderMut = useDeleteFolderMutation();
  const [createOpen, setCreateOpen] = useState(false);
  const [editFolder, setEditFolder] = useState<Folder | null>(null);
  const [deleteFolder, setDeleteFolder] = useState<Folder | null>(null);

  const onHome = location.pathname === '/';
  const view = params.get('view');
  const folderId = params.get('folder');

  const isUnlimited = sub.maxDiagrams === Infinity || !sub.maxDiagrams;
  const pct = isUnlimited
    ? 0
    : Math.min(100, Math.round((sub.diagramCount / sub.maxDiagrams) * 100));

  return (
    <nav className="flex h-full w-[216px] flex-none flex-col rounded-lg border border-border bg-sidebar py-2.5 shadow-sm">
      <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2.5">
        <NavLink to="/templates" className={({ isActive }) => `${base} ${isActive ? active : idle}`}>
          <LayoutTemplate className="h-[17px] w-[17px] flex-none opacity-80" />
          Templates
        </NavLink>
        <NavLink to="/nodes" className={({ isActive }) => `${base} ${isActive ? active : idle}`}>
          <Layers className="h-[17px] w-[17px] flex-none opacity-80" />
          My Nodes
        </NavLink>

        <GroupLabel>Library</GroupLabel>
        <Link to="/?view=all" className={`${base} ${onHome && view === 'all' ? active : idle}`}>
          <Files className="h-[17px] w-[17px] flex-none opacity-80" />
          All diagrams
        </Link>
        <Link to="/?view=starred" className={`${base} ${onHome && view === 'starred' ? active : idle}`}>
          <Star className="h-[17px] w-[17px] flex-none opacity-80" />
          Starred
        </Link>
        <Link to="/?view=recent" className={`${base} ${onHome && view === 'recent' ? active : idle}`}>
          <Clock className="h-[17px] w-[17px] flex-none opacity-80" />
          Recent
        </Link>

        <GroupLabel
          action={
            <button
              onClick={() => setCreateOpen(true)}
              className="ss-press grid h-[18px] w-[18px] place-items-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="New folder"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          }
        >
          Folders
        </GroupLabel>
        {folders.length === 0 && (
          <button
            onClick={() => setCreateOpen(true)}
            className="ss-press mx-0.5 mt-0.5 rounded-md border border-dashed border-border px-2.5 py-2 text-left text-[12px] text-muted-foreground transition-colors hover:border-border/80 hover:text-foreground"
          >
            + New folder
          </button>
        )}
        {folders.map((f) => (
          <FolderContextMenu
            key={f.id}
            onRename={() => setEditFolder(f)}
            onNewFolder={() => setCreateOpen(true)}
            onDelete={() => setDeleteFolder(f)}
          >
            <div className="group/folder relative flex items-center">
              <Link
                to={`/?folder=${f.id}`}
                className={`${base} min-w-0 flex-1 pr-7 ${onHome && folderId === f.id ? active : idle}`}
              >
                <DiagramIcon name={f.icon} color={f.color} className="h-[17px] w-[17px] flex-none" />
                <span className="truncate">{f.name}</span>
              </Link>
              <div className="absolute right-1.5">
                <FolderMenu name={f.name} onRename={() => setEditFolder(f)} onDelete={() => setDeleteFolder(f)} />
              </div>
            </div>
          </FolderContextMenu>
        ))}
      </div>

      <div className="mt-2 border-t border-border px-2.5 pt-2">
        <div className="px-1 pb-1.5 pt-1">
          <div className="mb-1.5 flex items-baseline justify-between text-[11.5px] text-muted-foreground">
            <span>
              <b className="text-[12px] font-semibold text-foreground">{sub.diagramCount}</b> of{' '}
              {isUnlimited ? '∞' : sub.maxDiagrams} diagrams
            </span>
            <span className="capitalize">{sub.tier}</span>
          </div>
          <div className="h-[5px] overflow-hidden rounded-full bg-muted">
            {isUnlimited ? (
              <div className="ss-usage-unlimited h-full w-full rounded-full" />
            ) : (
              <div className="h-full rounded-full bg-amber-500 transition-[width] duration-300" style={{ width: `${pct}%` }} />
            )}
          </div>
          {sub.isFreeTier && (
            <Link to="/pricing" className="ss-press mt-2 inline-block text-[11.5px] font-semibold text-primary">
              Upgrade to Pro →
            </Link>
          )}
        </div>
        <NavLink to="/billing" className={({ isActive }) => `${base} ${isActive ? active : idle}`}>
          <CreditCard className="h-[17px] w-[17px] flex-none opacity-80" />
          Billing
        </NavLink>
        <NavLink to="/settings" className={({ isActive }) => `${base} ${isActive ? active : idle}`}>
          <Settings className="h-[17px] w-[17px] flex-none opacity-80" />
          Settings
        </NavLink>
      </div>

      <CreateFolderModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={(data) => createFolderMut.mutateAsync(data).then(() => undefined)}
      />
      <EditFolderModal
        isOpen={!!editFolder}
        onClose={() => setEditFolder(null)}
        folder={editFolder}
        onSave={(id, data) => updateFolderMut.mutateAsync({ id, data }).then(() => undefined)}
      />
      <DeleteConfirmModal
        isOpen={!!deleteFolder}
        onClose={() => setDeleteFolder(null)}
        onConfirm={async () => {
          if (!deleteFolder) return;
          await deleteFolderMut.mutateAsync(deleteFolder.id);
          setDeleteFolder(null);
        }}
        title="Delete folder"
        message={
          deleteFolder
            ? `Delete "${deleteFolder.name}"? Diagrams inside won't be deleted — they'll just be moved out of the folder.`
            : ''
        }
        confirmText="Delete folder"
        isDeleting={deleteFolderMut.isPending}
      />
    </nav>
  );
}
